/**
 * arbi agentconfig list                     — list saved config versions
 * arbi agentconfig get [id]                 — show config (picker if no ID, 'default' for system)
 * arbi agentconfig save [json]              — save new config (interactive if no JSON)
 * arbi agentconfig delete [id]              — delete config (picker if no ID)
 * arbi agentconfig schema                   — dump JSON schema for all config models
 */

import { Command } from 'commander'
import { agentconfig as acOps } from '@arbidocs/sdk'
import { runAction, resolveAuth, printTable, parseJsonArg } from '../helpers.js'
import { promptSelect, promptInput, promptConfirm } from '../prompts.js'
import { success, ref } from '../output.js'

/** Config sections that have a MODEL_NAME field — the most common thing to change */
const MODEL_SECTIONS = [
  'Agents',
  'QueryLLM',
  'EvaluatorLLM',
  'TitleLLM',
  'SummariseLLM',
  'DoctagLLM',
  'ArtifactLLM',
] as const

/** Pick a config version from saved versions */
async function pickConfig(
  arbi: Awaited<ReturnType<typeof resolveAuth>>['arbi'],
  message = 'Select configuration'
) {
  const data = await acOps.listConfigs(arbi)
  if (data.versions.length === 0) {
    console.log('No saved configurations found.')
    process.exit(0)
  }
  return promptSelect(
    message,
    data.versions.map((v) => ({
      name: `${v.title || '(untitled)'} — ${new Date(v.created_at).toLocaleString()}`,
      value: v.external_id,
      description: v.external_id,
    }))
  )
}

/** Fetch available models for interactive picker */
async function fetchModels(arbi: Awaited<ReturnType<typeof resolveAuth>>['arbi']) {
  const data = await acOps.getModels(arbi)
  return data.models.map((m) => ({
    name: `${m.model_name} (${m.provider ?? m.api_type})`,
    value: m.model_name,
    description: m.api_type,
  }))
}

/** Interactive config builder — walks through most common settings */
async function buildConfigInteractively(
  arbi: Awaited<ReturnType<typeof resolveAuth>>['arbi']
): Promise<Record<string, unknown>> {
  const body: Record<string, unknown> = {}

  // Title
  const title = await promptInput('Config title (optional)', false)
  if (title) body.title = title

  // Pick section to configure
  const section = await promptSelect('Section to configure', [
    { name: 'Agents — main agent model, persona, tools', value: 'Agents' },
    { name: 'QueryLLM — answer review model', value: 'QueryLLM' },
    { name: 'EvaluatorLLM — chunk evaluation', value: 'EvaluatorLLM' },
    { name: 'TitleLLM — title generation', value: 'TitleLLM' },
    { name: 'SummariseLLM — conversation summariser', value: 'SummariseLLM' },
    { name: 'DoctagLLM — AI document tagging', value: 'DoctagLLM' },
    { name: 'ArtifactLLM — skill/memory synthesis', value: 'ArtifactLLM' },
    { name: 'Retriever — search parameters', value: 'Retriever' },
    { name: 'Reranker — reranking model', value: 'Reranker' },
    { name: 'ModelCitation — citation thresholds', value: 'ModelCitation' },
    { name: 'Chunker — chunk size', value: 'Chunker' },
    { name: 'Embedder — embedding model', value: 'Embedder' },
    { name: 'KeywordEmbedder — BM25 parameters', value: 'KeywordEmbedder' },
  ])

  const sectionConfig: Record<string, unknown> = {}

  // Sections with MODEL_NAME get a model picker
  if ((MODEL_SECTIONS as readonly string[]).includes(section)) {
    const models = await fetchModels(arbi)
    if (models.length > 0) {
      const changeModel = await promptConfirm('Change model?', true)
      if (changeModel) {
        const modelKey = section === 'Agents' ? 'AGENT_MODEL_NAME' : 'MODEL_NAME'
        sectionConfig[modelKey] = await promptSelect('Model', models)

        if (section === 'Agents') {
          sectionConfig.AGENT_API_TYPE = 'remote'
        } else {
          sectionConfig.API_TYPE = 'remote'
        }
      }
    }
  }

  // Section-specific fields
  if (section === 'Agents') {
    const field = await promptSelect('What else to change?', [
      { name: 'Nothing else', value: 'done' },
      { name: 'Temperature', value: 'LLM_AGENT_TEMPERATURE' },
      { name: 'Max tokens', value: 'AGENT_MAX_TOKENS' },
      { name: 'Max iterations', value: 'AGENT_MAX_ITERATIONS' },
      { name: 'Persona', value: 'PERSONA' },
      { name: 'Web search enabled', value: 'WEB_SEARCH' },
      { name: 'Human in the loop', value: 'HUMAN_IN_THE_LOOP' },
    ])
    if (field !== 'done') {
      if (field === 'WEB_SEARCH' || field === 'HUMAN_IN_THE_LOOP') {
        sectionConfig[field] = await promptConfirm(field + '?')
      } else if (field === 'LLM_AGENT_TEMPERATURE') {
        sectionConfig[field] = parseFloat(await promptInput('Temperature (0.0 - 2.0)'))
      } else if (field === 'AGENT_MAX_TOKENS' || field === 'AGENT_MAX_ITERATIONS') {
        sectionConfig[field] = parseInt(await promptInput(field), 10)
      } else {
        sectionConfig[field] = await promptInput(field)
      }
    }
  } else if (section === 'Retriever') {
    const field = await promptSelect('Field to change', [
      { name: 'Search mode', value: 'SEARCH_MODE' },
      { name: 'Min similarity score', value: 'MIN_RETRIEVAL_SIM_SCORE' },
      { name: 'Max chunks to retrieve', value: 'MAX_TOTAL_CHUNKS_TO_RETRIEVE' },
      { name: 'Max distinct documents', value: 'MAX_DISTINCT_DOCUMENTS' },
      { name: 'Hybrid dense weight', value: 'HYBRID_DENSE_WEIGHT' },
      { name: 'Hybrid sparse weight', value: 'HYBRID_SPARSE_WEIGHT' },
    ])
    if (field === 'SEARCH_MODE') {
      sectionConfig[field] = await promptSelect('Search mode', [
        { name: 'Semantic (dense)', value: 'semantic' },
        { name: 'Keyword (sparse/BM25)', value: 'keyword' },
        { name: 'Hybrid (dense + sparse)', value: 'hybrid' },
      ])
    } else {
      sectionConfig[field] = parseFloat(await promptInput(field))
    }
  } else if (section === 'ModelCitation') {
    const field = await promptSelect('Field to change', [
      { name: 'Similarity threshold', value: 'SIM_THREASHOLD' },
      { name: 'Min char size', value: 'MIN_CHAR_SIZE_TO_ANSWER' },
      { name: 'Max citations', value: 'MAX_NUMB_CITATIONS' },
    ])
    sectionConfig[field] = parseFloat(await promptInput(field))
  } else if (section === 'Chunker') {
    sectionConfig.MAX_CHUNK_TOKENS = parseInt(await promptInput('Max chunk tokens'), 10)
  } else if (section === 'Reranker') {
    const field = await promptSelect('Field to change', [
      { name: 'Max chunks after rerank', value: 'MAX_NUMB_OF_CHUNKS' },
      { name: 'Model', value: 'MODEL_NAME' },
    ])
    if (field === 'MODEL_NAME') {
      const models = await fetchModels(arbi)
      sectionConfig.MODEL_NAME = await promptSelect('Model', models)
      sectionConfig.API_TYPE = 'remote'
    } else {
      sectionConfig[field] = parseInt(await promptInput(field), 10)
    }
  } else if (
    section === 'QueryLLM' ||
    section === 'EvaluatorLLM' ||
    section === 'TitleLLM' ||
    section === 'SummariseLLM' ||
    section === 'DoctagLLM' ||
    section === 'ArtifactLLM'
  ) {
    // LLM sections share temperature/max_tokens
    const field = await promptSelect('What else to change?', [
      { name: 'Nothing else', value: 'done' },
      { name: 'Temperature', value: 'TEMPERATURE' },
      { name: 'Max tokens', value: 'MAX_TOKENS' },
    ])
    if (field !== 'done') {
      sectionConfig[field] =
        field === 'TEMPERATURE'
          ? parseFloat(await promptInput('Temperature (0.0 - 2.0)'))
          : parseInt(await promptInput('Max tokens'), 10)
    }
  }

  if (Object.keys(sectionConfig).length > 0) {
    body[section] = sectionConfig
  }

  return body
}

export function registerAgentconfigCommand(program: Command): void {
  const ac = program
    .command('agentconfig')
    .description('Agent configurations: list, get, save, delete, schema')

  ac.command('list')
    .description('List saved configuration versions')
    .action(
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await acOps.listConfigs(arbi)

        if (data.versions.length === 0) {
          console.log('No saved configurations.')
          return
        }

        printTable(
          [
            { header: 'ID', width: 24, value: (r) => r.external_id as string },
            { header: 'TITLE', width: 30, value: (r) => (r.title as string) ?? '(untitled)' },
            {
              header: 'CREATED',
              width: 22,
              value: (r) => new Date(r.created_at as string).toLocaleString(),
            },
          ],
          data.versions
        )
      })
    )

  ac.command('get [config-id]')
    .description('Show configuration details (picker if no ID; use "default" for system defaults)')
    .action((configId?: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        if (!configId) {
          configId = await promptSelect('Which configuration?', [
            { name: 'System defaults', value: 'default', description: 'Built-in defaults' },
            { name: 'Pick from saved versions...', value: '__pick__' },
          ])
          if (configId === '__pick__') {
            configId = await pickConfig(arbi)
          }
        }
        const data = await acOps.getConfig(arbi, configId)
        console.log(JSON.stringify(data, null, 2))
      })()
    )

  ac.command('save [json]')
    .description('Save a new configuration (interactive if no JSON given)')
    .option('-t, --title <title>', 'Config title')
    .option('--tag <tag-ext-id>', 'Associate with an assistant tag')
    .option('--message <msg-ext-id>', 'Associate with a parent message')
    .action((json?: string, opts?: { title?: string; tag?: string; message?: string }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()

        let body: Record<string, unknown>
        if (json) {
          body = parseJsonArg(
            json,
            'arbi agentconfig save \'{"Agents": {"AGENT_MODEL_NAME": "GPT4o@OpenAI"}, "title": "GPT-4o config"}\''
          )
          if (opts?.title) body.title = opts.title
        } else {
          body = await buildConfigInteractively(arbi)
        }

        if (opts?.tag) body.tag_ext_id = opts.tag
        if (opts?.message) body.parent_message_ext_id = opts.message

        const data = await acOps.saveConfig(arbi, body as Parameters<typeof acOps.saveConfig>[1])
        success(`Saved: ${data.title || '(untitled)'} (${ref(data.external_id)})`)
      })()
    )

  ac.command('delete [config-id]')
    .description('Delete a configuration (picker if no ID)')
    .action((configId?: string) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        if (!configId) configId = await pickConfig(arbi, 'Select configuration to delete')
        const data = await acOps.deleteConfig(arbi, configId)
        success(data.detail)
      })()
    )

  ac.command('schema')
    .description('Show JSON schema for all configuration models')
    .action(
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await acOps.getSchema(arbi)
        console.log(JSON.stringify(data, null, 2))
      })
    )

  // Default action: list
  ac.action(async () => {
    await ac.commands.find((c) => c.name() === 'list')!.parseAsync([], { from: 'user' })
  })
}
